import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUserPortfolio } from '../../hooks/contract/useUserPortfolio';
import { useAllPlans, FALLBACK_PLANS } from '../../hooks/contract/useAllPlans';
import DepositRow from '../../components/dashboard/DepositRow';
import { isMatured, planDurationSec } from '../../lib/format';

const FILTERS = [
  { id: 'all', key: 'dashboard.deposits.filter.all' },
  { id: 'active', key: 'dashboard.deposits.filter.active' },
  { id: 'matured', key: 'dashboard.deposits.filter.matured' },
  { id: 'closed', key: 'dashboard.deposits.filter.closed' },
];

const Deposits = () => {
  const { t } = useTranslation();
  const { deposits, isLoading } = useUserPortfolio();
  const { data: onChainPlans } = useAllPlans();
  const plans = onChainPlans ?? FALLBACK_PLANS;

  const [filter, setFilter] = useState('all');

  const now = Math.floor(Date.now() / 1000);

  const filtered = useMemo(() => {
    if (!deposits) return [];
    return deposits
      .map((d, idx) => ({ d, idx }))
      .filter(({ d }) => {
        const plan = plans[Number(d.planId)];
        if (filter === 'all') return true;
        if (filter === 'closed') return !d.active;
        if (filter === 'active') {
          return (
            d.active &&
            plan &&
            !isMatured({ startedAt: d.startedAt, durationSec: planDurationSec(plan), now })
          );
        }
        if (filter === 'matured') {
          return (
            d.active &&
            plan &&
            isMatured({ startedAt: d.startedAt, durationSec: planDurationSec(plan), now })
          );
        }
        return true;
      });
  }, [deposits, plans, filter, now]);

  return (
    <div className="db-page db-page--deposits">
      <div className="db-page__head">
        <h1 className="db-page__title">{t('dashboard.deposits.title')}</h1>
        <p className="db-page__subtitle">{t('dashboard.deposits.subtitle')}</p>
      </div>

      <div className="dep-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`dep-filters__btn${filter === f.id ? ' is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {t(f.key)}
          </button>
        ))}
        <Link to="/dashboard/new" className="sf-btn sf-btn--gold sf-btn--sm dep-filters__new">
          <i className="fas fa-plus" /> {t('dashboard.deposits.newBtn')}
        </Link>
      </div>

      {isLoading && (
        <div className="db-loading">
          <i className="fas fa-circle-notch fa-spin" /> {t('dashboard.common.loading')}
        </div>
      )}

      {!isLoading && !filtered.length && (
        <div className="db-empty">
          <div className="db-empty__icon">
            <i className="fas fa-layer-group" />
          </div>
          <h3>{t('dashboard.deposits.empty.title')}</h3>
          <p>{t('dashboard.deposits.empty.desc')}</p>
          <Link to="/dashboard/new" className="sf-btn sf-btn--gold">
            {t('dashboard.deposits.empty.cta')}
          </Link>
        </div>
      )}

      <div className="dep-list">
        {filtered.map(({ d, idx }) => (
          <DepositRow
            key={idx}
            deposit={d}
            depositIdx={idx}
            plan={plans[Number(d.planId)]}
            plans={plans}
          />
        ))}
      </div>
    </div>
  );
};

export default Deposits;
